import type {
    SketchDimensionSnapshot,
} from '../../sketch/dimension/SketchDimensionTypes';
import type {
    HistoryDomainAdapter,
    HistoryPatchApplyInput,
} from '../domain/HistoryDomainAdapter';
import type {
    EntityPatch,
    HistoryEntitySnapshot,
} from '../types';

/**
 * 草图尺寸历史 adapter ID。
 */
export const SKETCH_DIMENSION_HISTORY_ADAPTER_ID = 'history-domain:sketch-dimension';

/**
 * 草图尺寸历史运行时存储。
 *
 * 设计说明：
 * 真实实现由宿主把快照灌回 `SketchDimensionRepository`（restoreDimension /
 * removeDimensionForRestore），不得写入 Three.js 对象或渲染缓存。
 */
export interface SketchDimensionHistoryStore {
    /**
     * 写入尺寸快照。
     *
     * @param snapshot 尺寸完整快照。
     */
    readonly upsertSketchDimension: (snapshot: SketchDimensionSnapshot) => void;
    /**
     * 删除尺寸。
     *
     * @param dimensionId 尺寸持久 ID。
     */
    readonly deleteSketchDimension: (dimensionId: string) => void;
}

/**
 * 判断未知值是否为结构合法的尺寸快照。
 *
 * @param value 未知值。
 * @returns 是否为尺寸快照。
 */
export function isSketchDimensionSnapshot(value: unknown): value is SketchDimensionSnapshot {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return false;
    }

    const snapshot = value as Record<string, unknown>;

    return typeof snapshot.dimensionId === 'string'
        && typeof snapshot.sketchId === 'string'
        && typeof snapshot.name === 'string'
        && typeof snapshot.dimensionKind === 'string'
        && typeof snapshot.role === 'string'
        && Boolean(snapshot.parameter)
        && typeof snapshot.parameter === 'object'
        && Array.isArray(snapshot.references);
}

/**
 * 应用尺寸历史快照。
 *
 * @param input 补丁应用输入。
 */
function applySketchDimensionSnapshot(
    input: HistoryPatchApplyInput<SketchDimensionSnapshot, SketchDimensionHistoryStore>,
): void {
    const snapshot = input.snapshot;

    if (!snapshot.exists) {
        input.context.deleteSketchDimension(input.patch.entityId);

        return;
    }

    if (!snapshot.data) {
        throw new Error(`尺寸 ${input.patch.entityId} 历史快照缺少数据。`);
    }

    if (!isSketchDimensionSnapshot(snapshot.data)) {
        throw new Error(`尺寸 ${input.patch.entityId} 的历史快照不是合法尺寸主状态。`);
    }

    input.context.upsertSketchDimension(snapshot.data);
}

/**
 * 比较尺寸历史快照是否语义相等。
 *
 * 说明：快照统一由 `SketchDimension.toSnapshot()` 生成，字段构造顺序稳定，
 * 直接 JSON 序列化比较即可。
 *
 * @param left 左侧快照。
 * @param right 右侧快照。
 * @returns 是否语义相等。
 */
function areSketchDimensionHistorySnapshotsEqual(
    left: HistoryEntitySnapshot<SketchDimensionSnapshot>,
    right: HistoryEntitySnapshot<SketchDimensionSnapshot>,
): boolean {
    if (left.exists !== right.exists) {
        return false;
    }

    if (!left.exists || !right.exists) {
        return true;
    }

    if (!left.data || !right.data) {
        return false;
    }

    return JSON.stringify(left.data) === JSON.stringify(right.data);
}

/**
 * 创建草图尺寸历史 adapter。
 *
 * @returns 草图尺寸历史 adapter。
 */
export function createSketchDimensionHistoryAdapter(): HistoryDomainAdapter<
    SketchDimensionSnapshot,
    SketchDimensionHistoryStore
> {
    return {
        adapterId: SKETCH_DIMENSION_HISTORY_ADAPTER_ID,
        kind: 'sketch-dimension',
        applySnapshot: applySketchDimensionSnapshot,
        areSnapshotsEqual: areSketchDimensionHistorySnapshotsEqual as HistoryDomainAdapter<
            SketchDimensionSnapshot,
            SketchDimensionHistoryStore
        >['areSnapshotsEqual'],
    };
}

/**
 * 创建草图尺寸历史补丁。
 *
 * @param patch 补丁字段。
 * @returns 草图尺寸历史补丁。
 */
export function createSketchDimensionHistoryPatch(
    patch: Omit<EntityPatch<SketchDimensionSnapshot>, 'adapterId' | 'entityKind'>,
): EntityPatch<SketchDimensionSnapshot> {
    return {
        ...patch,
        entityKind: 'sketch-dimension',
        adapterId: SKETCH_DIMENSION_HISTORY_ADAPTER_ID,
    };
}
